import concat from 'lodash/concat';
import { ReactElement, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import Form from '~/ui/pages/ActTeam/reusable/Form';

import { CLINIC_MANAGEMENT, CLINIC_VIEW_ACT_TEAM, VIEW_CLINIC } from '~/ui/constants/paths';
import { IInitialValues } from '../types';

type IParams = {
  clinicId: string;
  actTeamId: string;
};

const breadcrumbItem = [
  {
    to: CLINIC_MANAGEMENT,
    title: 'Clinic Management',
  },
];

type ILocationState = { pathname: string; state: { from?: string } };

const EditActTeam = (): ReactElement => {
  const { clinicId, actTeamId } = useParams<IParams>();
  const { pathname, state: locationState }: ILocationState = useLocation();
  const navigate = useNavigate();

  const actTeam = useStoreState(state => state.actTeam.current);
  const clinic = useStoreState(state => state.clinic.current);

  const onGetClinic = useStoreActions(actions => actions.clinic.onGetClinic);
  const onGetClinicActTeam = useStoreActions(actions => actions.actTeam.onGetClinicActTeam);
  const onEditClinicActTeam = useStoreActions(actions => actions.actTeam.onEditClinicActTeam);

  const cancelLinkTo = useMemo(() => {
    if (locationState?.from) return locationState.from;

    return {
      pathname: VIEW_CLINIC.replace(':id', clinicId),
      state: { selectedTab: 1 },
    };
  }, [clinicId, locationState?.from]);

  const { isLoading } = useApiRequest(
    [() => onGetClinicActTeam({ clinicId, actTeamId }), () => onGetClinic(clinicId)],
    { initialLoading: true, dependencies: [clinicId, actTeamId] },
  );

  const { mutate: onSubmit, isLoading: isUpdating } = useApiMutation(
    ({ typeId, name, capacity, isShelterTeam, isCrisisTeam }: IInitialValues) =>
      onEditClinicActTeam({
        clinicId,
        actTeam: { id: Number(actTeamId), typeId, name, capacity, isShelterTeam, isCrisisTeam },
      }),
    {
      notifyMessage: 'Team successfully updated',
      onSuccess: () =>
        navigate(
          CLINIC_VIEW_ACT_TEAM.replace(':clinicId', clinicId).replace(':actTeamId', actTeamId),
        ),
      throwOnError: false,
    },
  );

  if (isLoading || !actTeam || !clinic) return <Loader />;

  const breadcrumbItems = concat(breadcrumbItem, [
    {
      title: clinic?.name,
      to: VIEW_CLINIC.replace(':id', clinicId),
    },
    {
      title: actTeam?.name,
      to: CLINIC_VIEW_ACT_TEAM.replace(':clinicId', clinicId).replace(':actTeamId', actTeamId),
    },
    {
      title: 'Edit Team',
      to: pathname,
    },
  ]);

  const defaultValues = {
    name: actTeam?.name,
    typeId: actTeam?.type.id,
    clinicId,
    capacity: actTeam?.capacity,
    isShelterTeam: !!actTeam?.isShelterTeam,
    isCrisisTeam: !!actTeam?.isCrisisTeam,
  };

  const leavePageDescription = 'Are you sure you want to discard changes?';

  return (
    <div>
      <h2>Edit {actTeam?.name}</h2>
      <Breadcrumbs itemsForBreadcrumbs={breadcrumbItems} />
      <Form
        isEdit
        defaultValues={defaultValues}
        clinicSelectOptions={[
          { value: clinicId, label: clinic?.name, hasCrisisTeam: clinic?.hasCrisisTeam },
        ]}
        isProcessing={isUpdating}
        cancelLinkTo={cancelLinkTo}
        leavePageDescription={leavePageDescription}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default EditActTeam;
